import { ReactNode } from 'react';
import Markdown from 'react-markdown';

import { FaiProductsCharacteristicsType } from './fai-comparator-service';

export type Characteristic = {
    name: ReactNode;
    imageUrl?: string;
    key: string;
    items: ProductCharacteristic[];
};

export type ProductCharacteristic = {
    name: string;
    items: ProductCharacteristicItems[];
};

export type ProductCharacteristicItems = { key: string; children: ReactNode; name: string };

type ComparatorConfigItemEntry = {
    item_slug: string;
    item_order: number;
    item_name: string;
};

type ComparatorConfigCategory = {
    category_title: string;
    category_image_url?: string;
    category_order: number;
    category_slug: string;
    category_items?: { items: ComparatorConfigItemEntry[] };
};

export type ComparatorConfig = {
    items?: ComparatorConfigCategory[];
};

export function groupFaiCharacteristics(
    products: FaiProductsCharacteristicsType,
    comparatorConfig?: ComparatorConfig,
): Characteristic[] {
    if (!products?.length) return [];

    if (comparatorConfig?.items?.length) {
        const categories = [...comparatorConfig.items].sort(
            (a, b) => (a.category_order ?? 0) - (b.category_order ?? 0),
        );

        const mapped: Characteristic[] = categories.map((cat) => {
            const title = <Markdown>{cat.category_title || cat.category_slug}</Markdown>;
            const entries = [...(cat.category_items?.items || [])].sort(
                (a, b) => (a.item_order ?? 0) - (b.item_order ?? 0),
            );
            const rows: ProductCharacteristic[] = entries
                .filter((entry) =>
                    products.some((product) => {
                        const comparatorEntry = product.comparator?.find((c) => c.item === entry.item_slug);

                        return !!comparatorEntry?.value;
                    }),
                )
                .map((entry) => {
                    const items: ProductCharacteristicItems[] = products.map((product) => {
                        const comparatorEntry = product.comparator?.find((c) => c.item === entry.item_slug);

                        const renderChildren = () => {
                            if (comparatorEntry?.booleanTrueValue) {
                                return (
                                    <div className='m-auto'>
                                        <span className={'icon is-small is-success'}>
                                            <i className='tri-check' aria-hidden='true'></i>
                                        </span>
                                    </div>
                                );
                            }

                            if (comparatorEntry?.value) {
                                return <Markdown>{comparatorEntry.value}</Markdown>;
                            }

                            return (
                                <div className='m-auto'>
                                    <span className='icon is-small is-grey'>
                                        <i className='tri-times' aria-hidden='true'></i>
                                    </span>
                                </div>
                            );
                        };

                        return {
                            key: `${product.gencode}-${entry.item_slug}`,
                            name: product.gencode,
                            children: renderChildren(),
                        };
                    });

                    return { name: entry.item_name, items };
                });

            return { name: title, key: cat.category_slug, imageUrl: cat.category_image_url, items: rows };
        });

        const nonEmpty = mapped.filter((cat) => cat.items.length > 0);

        if (nonEmpty.length) {
            return nonEmpty;
        }
    }

    return [];
}
